import React, { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'
import dt from 'dayjs'
import * as lib from '../lib'
import * as state from '../states'
import {
	PageTitle,
	OfferSpendByAccountListTable,
	OfferSpendByAccountDetailsModal,
	ToolPanel,
	UiPagination,
	ESortOrder
} from '../components'
import { Filters, FiltersButtonIcon } from '../components/filters'
import { useOfferSpendByAccountList, useOfferSpendByAccountFiltersPage, useOfferSpendByAccountSortPage } from '../hooks'
import {
	type IFetchItemsSetup,
	EOfferSpendByAccountView,
	ERoleAccess,
	TOfferSpendByAccountDay,
	TOfferSpendByAccountAggOnly,
	OFFER_SPEND_BY_ACCOUNT_VIEW_LABEL_MAP
} from '../types'
import { TAKE, MENU } from '../constants'
import { useTranslation } from 'react-i18next'

const buildRange = (
	by: EOfferSpendByAccountView,
	at: [string | null, string | null]
): Array<dt.Dayjs> => {
	const unit = by
	if (
		Array.isArray(at)
		&&
		at.length === 2
		&&
		at.every(_ => typeof _ === 'string' && _.length > 0)
		&&
		at.every(_ => dt(_).isValid())
	) {
		const [a, b] = at.map(_ => dt.utc(_).startOf(unit))
		const diff = Math.ceil(b.diff(a, unit, true))
		if (diff >= 0) {
			const range: Array<dt.Dayjs> = Array.from(
				{ length: diff + 1 },
				(_, item: number): dt.Dayjs => a.add(item, unit)
			)
			return range
		}
	}
	return []
}

export const OfferSpendByAccountPage = (): React.JSX.Element => {
	const { t } = useTranslation()
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null

	const profile = useAtomValue(state.profileAtom)
	const $access = new lib.Access(profile ?? null)

	const location = useLocation()
	const href = lib.pageHref(location, TAKE)
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()

	const viewBy = lib.offerSpendViewByAccountSearchParams(searchParams.get('by')) ?? EOfferSpendByAccountView.Day
	const page = lib.skipSearchParams(searchParams.get('page')) ?? 0
	const take = lib.takeSearchParams(searchParams.get('take')) ?? TAKE
	const skip = (take === 'all') ? 0 : page > 0 ? page * take : 0

	const pageTitle = `${t(MENU.offerSpendReport.title)} ${t(MENU.offerSpendReportByAccount.title)}`
	const viewByKeyTrans = OFFER_SPEND_BY_ACCOUNT_VIEW_LABEL_MAP?.[viewBy]
	const viewByLabelTrans = t(viewByKeyTrans)
	const pageTitleFormat = `${pageTitle}${viewBy ? ': ' + viewByLabelTrans : ''}`

	const {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		offerSpendByAccountFiltersConfig,
		defaultFiltersValue,
		initialFiltersValue,
		clearChildFilters,
		setUserFilters
	} = useOfferSpendByAccountFiltersPage()

	//TODO: check sort output var type ?
	const {
		// sort: sortTmp,
		sortBy,
		setSortBy,
		order,
		setOrder
	} = useOfferSpendByAccountSortPage() // ?!?!

	const setup: IFetchItemsSetup = {
		filter,
		sort: order !== ESortOrder.unset ? [[sortBy, order]] : [],
		// sort: [], // ?!?!
		skip,
		take
	}
	const { error, items, total, warmUp } = useOfferSpendByAccountList(setup, send)

	React.useEffect(() => {
		console.log({ filter })
	}, [filter])

	const range: Array<dt.Dayjs> = buildRange(viewBy, filter.find(([k]) => k === 'at+between')?.[1])

	const [viewOfferSpendDetailsInfo, setViewOfferSpendDetailsInfo] = useState<TOfferSpendByAccountDay | TOfferSpendByAccountAggOnly | null>(null)

	const onViewOfferSpendDetailsInfo = (item: TOfferSpendByAccountDay | TOfferSpendByAccountAggOnly, e: React.MouseEvent) => {
		e.preventDefault()
		setViewOfferSpendDetailsInfo(item)
	}

	return (
		<>
			<PageTitle title={pageTitleFormat}/>

			<div className="card rounded-3 border-0 mb-24">
				<div className="card-body p-25">
					{/* remove CREATE button for 'account' main page (without pre-selected group) */}
					<div className="d-flex align-items-center justify-content-between pb-20 border-bottom border-color">
						<FiltersButtonIcon
							activeFiltersCount={activeFiltersCount}
							filterFillFactor={filterFillFactor}
							onClick={() => setIsFiltersPanelOpen((val) => !val)}
						/>
					</div>

					<Filters
						config={offerSpendByAccountFiltersConfig}
						initialFiltersValue={initialFiltersValue}
						resetFiltersValue={defaultFiltersValue}
						isAutoModeWithDebounce={false}
						isFiltersPanelOpen={isFiltersPanelOpen}
						isFiltersToClear={clearChildFilters}
						setFiltersCB={(value) => {
							//console.log('page.RolePage::setFiltersCB::', { value })
							setUserFilters(value)
						}}
					/>

					<OfferSpendByAccountListTable
						viewBy={viewBy}
						range={range}
						items={items}
						skip={skip}
						sortBy={sortBy} // ?!?!
						setSortBy={setSortBy}
						order={order}
						setOrder={setOrder}
						onViewOfferSpendDetails={onViewOfferSpendDetailsInfo}
					/>

					<ToolPanel flow="<-">
						<UiPagination
							classesToAdd={{
								wrapper: 'mt-25'
							}}
							page={page}
							take={take}
							total={total}
							href={(page: number) => href(page).tail}
						/>
					</ToolPanel>
				</div>
			</div>

			{
				!!viewOfferSpendDetailsInfo && !!$access.$(ERoleAccess.OfferSpendByAccountList) && <OfferSpendByAccountDetailsModal
					viewBy={viewBy}
					offerSpend={viewOfferSpendDetailsInfo}
					show={!!viewOfferSpendDetailsInfo}
					take={take}
					onClose={() => {
						setViewOfferSpendDetailsInfo(null)
						// navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

		</>
	)
}
